import { Component, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { Store } from '@ngrx/store';
import { Subscription, interval, EmptyError } from 'rxjs';
import { takeWhile, filter, switchMap, first, take, throwIfEmpty } from 'rxjs/operators';

import { UpgradeFinalStatus, GetUpgradeStatusResponse } from '../../api/models';
import { State, Stage, UpgradeError } from '../../interfaces';
import { StatusActions } from '../../store/actions';
import { UpgradeFlightsService } from '../../services/upgrade-flights.service';

export const POLLING_DONE_STATUSES: UpgradeFinalStatus[] = [
  UpgradeFinalStatus.Failed,
  UpgradeFinalStatus.PaymentFailed,
  UpgradeFinalStatus.HandledByCustomerCare,
  UpgradeFinalStatus.Success,
  UpgradeFinalStatus.PaymentInProgress,
  UpgradeFinalStatus.InProgress,
  UpgradeFinalStatus.PaymentDone,
];
export const GET_STATUS_REPEAT_AMOUNT = 50;
export const GET_STATUS_REPEAT_INTERVAL_MS = 1000;

@Component({
  selector: 'fin-upgrade-processing',
  templateUrl: 'processing.component.html',
})
export class ProcessingComponent implements OnDestroy {
  subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<State>,
    private upgradeService: UpgradeFlightsService,
    @Inject(PLATFORM_ID) private platform: object
  ) {
    this.checkUpgradeStatus();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  checkUpgradeStatus(): void {
    if (isPlatformServer(this.platform)) {
      // Skip processing on server-side
      return;
    }

    const getUpgradeStatus$ = this.upgradeService.getUpgradeStatus().pipe(first());

    this.subscriptions.add(
      interval(GET_STATUS_REPEAT_INTERVAL_MS)
        .pipe(
          switchMap(() => getUpgradeStatus$),
          take(GET_STATUS_REPEAT_AMOUNT),
          takeWhile((status) => this.paymentIsInitializing(status), true),
          filter((response) => !this.paymentIsInitializing(response)),
          throwIfEmpty(() => new EmptyError())
        )
        .subscribe({
          next: (response) => {
            switch (response.status) {
              case UpgradeFinalStatus.Success:
              case UpgradeFinalStatus.PaymentInProgress:
              case UpgradeFinalStatus.PaymentDone:
              case UpgradeFinalStatus.InProgress:
              case UpgradeFinalStatus.HandledByCustomerCare: {
                this.store$.dispatch(StatusActions.setStage({ stage: Stage.SUCCESS }));
                return;
              }
              case UpgradeFinalStatus.Failed:
              case UpgradeFinalStatus.PaymentFailed: {
                this.store$.dispatch(StatusActions.setStage({ stage: Stage.ERROR }));
                return;
              }
              default: {
                return;
              }
            }
          },
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          error: (error: EmptyError | UpgradeError) => {
            if (error === UpgradeError.httpError) {
              this.store$.dispatch(StatusActions.setStage({ stage: Stage.ERROR }));
            } else {
              // If we don't get correct status in time, redirect user to success to see message that upgrade is prosessing.
              // This shouldn't happen but just in case to prevent user from spamming the endpoint forver.
              this.store$.dispatch(StatusActions.setStage({ stage: Stage.SUCCESS }));
            }
          },
        })
    );
  }

  private paymentIsInitializing(response: GetUpgradeStatusResponse): boolean {
    return !POLLING_DONE_STATUSES.includes(response.status);
  }
}
